/*
 * Regional Back in Stock
 *
 * Tar över skickandet i temats Notify me-ruta (NotifyMe i global.min.js):
 *  - prenumerationen läggs på Klaviyos regionala variant, <variant-id>-US eller
 *    -EU, så att mejlet följer lagret som skickar till kundens land
 *  - valfritt telefonnummer sparas på profilen
 *  - kryssrutorna lägger till e-post i nyhetsbrevslistan och telefon i SMS-listan
 *
 * Lyssnar i capture-fasen och stoppar eventet, så temats egen hanterare aldrig
 * körs. Visning av meddelanden sker via rutans egna metoder.
 */
(() => {
  const REVISION = '2025-07-15';
  const POPUP = 'notify-me-popup[data-bis-suffix]';

  // Landsnummer för butikens marknader. Används när kunden skriver numret utan +.
  const DIAL = {
    US: '1', CA: '1', MX: '52', AE: '971', SE: '46', DK: '45', NO: '47', FI: '358',
    DE: '49', AT: '43', CH: '41', FR: '33', BE: '32', NL: '31', IT: '39', ES: '34',
    PT: '351', GB: '44', IE: '353', PL: '48', CZ: '420', LT: '370', LV: '371',
    EE: '372', HR: '385', BG: '359'
  };

  // Returnerar E.164 (+46701234567), '' om fältet är tomt, null om ogiltigt.
  function normalizePhone(raw, country) {
    let p = raw.replace(/[\s\-().\/]/g, '');
    if (!p) return '';
    if (p.startsWith('00')) p = '+' + p.slice(2);
    if (!p.startsWith('+')) {
      const code = DIAL[country];
      if (!code) return null;
      if (code === '1' && p.length === 11 && p.startsWith('1')) p = p.slice(1);
      p = '+' + code + p.replace(/^0+/, '');
    }
    return /^\+[1-9]\d{6,14}$/.test(p) ? p : null;
  }

  async function post(path, key, body) {
    const res = await fetch(`https://a.klaviyo.com/client/${path}/?company_id=${encodeURIComponent(key)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: REVISION },
      body: JSON.stringify(body)
    });
    if (res.status !== 200 && res.status !== 202) throw new Error(`Klaviyo ${res.status}`);
  }

  function bisBody(variantId, profile) {
    return {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: { data: { type: 'profile', attributes: profile } }
        },
        relationships: {
          variant: { data: { type: 'catalog-variant', id: `$custom:::$default:::${variantId}` } }
        }
      }
    };
  }

  function listBody(listId, profile, channel) {
    return {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: { ...profile, subscriptions: { [channel]: { marketing: { consent: 'SUBSCRIBED' } } } }
            }
          }
        },
        relationships: { list: { data: { type: 'list', id: listId } } }
      }
    };
  }

  function showPhoneError(el) {
    el.clearNotifications();
    el.querySelector('.product-module__notify-action--phone-error')?.setAttribute('aria-hidden', 'false');
  }

  async function submit(el) {
    if (el.dataset.bisBusy) return;
    const value = (name) => (el.querySelector(`[name="${name}"]`)?.value || '').trim();
    const checked = (name) => !!el.querySelector(`[name="${name}"]`)?.checked;

    const email = value('notify_email');
    if (!email || !email.includes('@')) return el.showNotification('email-error');

    const phone = normalizePhone(value('notify_phone'), el.dataset.country);
    if (phone === null) return showPhoneError(el);

    const variantInput = document.querySelector('form[data-product-form] [name="id"]');
    if (!variantInput) return el.showNotification('error');
    const variantId = `${variantInput.value}-${el.dataset.bisSuffix}`;
    const key = el.dataset.key;

    el.dataset.bisBusy = '1';
    try {
      const profile = phone ? { email, phone_number: phone } : { email };
      try {
        await post('back-in-stock-subscriptions', key, bisBody(variantId, profile));
      } catch (err) {
        // Ett telefonnummer som redan hör till en annan profil kan ge avslag.
        // Prenumerationen är det viktiga, så försök igen med bara e-post.
        if (!phone) throw err;
        await post('back-in-stock-subscriptions', key, bisBody(variantId, { email }));
      }

      // Listorna får inte fälla prenumerationen, därför allSettled.
      const lists = [];
      if (checked('notify_email_consent') && el.dataset.emailList) {
        lists.push(post('subscriptions', key, listBody(el.dataset.emailList, { email }, 'email')));
      }
      if (phone && checked('notify_sms_consent') && el.dataset.smsList) {
        lists.push(post('subscriptions', key, listBody(el.dataset.smsList, { email, phone_number: phone }, 'sms')));
      }
      await Promise.allSettled(lists);

      el.showNotification('success');
    } catch (err) {
      el.showNotification('error');
    } finally {
      delete el.dataset.bisBusy;
    }
  }

  document.addEventListener('click', (e) => {
    const el = e.target.closest?.('[data-action="subscribe"]')?.closest(POPUP);
    if (!el) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    submit(el);
  }, true);

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' || e.target.type === 'checkbox') return;
    const el = e.target.closest?.(POPUP);
    if (!el) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    submit(el);
  }, true);

  // SMS-rutan visas först när ett telefonnummer är ifyllt.
  document.addEventListener('input', (e) => {
    if (e.target.name !== 'notify_phone') return;
    const box = e.target.closest(POPUP)?.querySelector('[data-bis-sms-consent]');
    if (box) box.hidden = !e.target.value.trim();
  });
})();
